import React, { useEffect, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ButtonGroup,
} from "reactstrap"
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
} from "reactstrap"
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined"
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined"
import PasswordOutlinedIcon from "@mui/icons-material/PasswordOutlined"
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined"
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined"
import PrimaryButton from "newcomponents/primary-button"
import * as Yup from "yup"
import { useFormik } from "formik"
import { Switch } from "@mui/material"
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"
import { setBreadcrumbItems } from "../../store/actions"
import withRouter from "components/Common/withRouter"
import { editProfile, resetProfileFlag } from "../../store/actions"
import { Spinner } from "reactstrap"
import config from "../../config/config"
import axios from "axios"
import SnackbarComponent from "components/SnackBar"
import MFAInput from "components/Common/MFAInput"
import userImage from "../../assets/images/user.jpg"


const UserProfile = () => {
  document.title = "Profile | Reputation Management"
  const [saveModalOpen, setSaveModalOpen] = useState(false)

  const userData = useSelector(state => state.Login.userData)
  const dispatch = useDispatch()

  const breadcrumbItems = [
    { title: `${userData.name}`, link: "/" },
    { title: "User Profile", link: "#" },
  ]
  useEffect(() => {
    dispatch(setBreadcrumbItems("Profile", breadcrumbItems))
  }, [])

  const [visible, setVisible] = useState(true)
  const [loading, setLoading] = useState(false)
  const [qrCodeUrl, setQrCodeUrl] = useState(null)
  const [qrModalOpen, setQrModalOpen] = useState(false)
  const [errors, setErrors] = useState([])
  const onDismiss = () => setVisible(false)
  const [twoFactorMethod, setTwoFactorMethod] = useState(
    userData?.twoFactorMethod || "Email",
  )
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  const [disable2faModalOpen, setDisable2faModalOpen] = useState(false)
  const [disable2faPassword, setDisable2faPassword] = useState("")
  const [disable2faCode, setDisable2faCode] = useState("")

  const [disable2faError, setDisable2faError] = useState("")
  const selectProfileState = state => state.Profile
  const ProfileProperties = createSelector(selectProfileState, profile => ({
    error: profile.error,
    success: profile.success,
  }))

  const { error, success } = useSelector(ProfileProperties)

  const [avatarPreview, setAvatarPreview] = useState(
    userData?.avatar || userImage,
  )
  const [avatarFile, setAvatarFile] = useState(null)
  const mode = useSelector(state => state.Layout.layoutMode)
  const [twoFaEnabled, setTwoFaEnabled] = useState(
    userData?.twoFactorEnabled || false,
  )

  const [mfaModalOpen, setMfaModalOpen] = useState(false)
  const [mfaError, setMfaError] = useState("")
  const [pendingProfileData, setPendingProfileData] = useState(null)
  useEffect(() => {
    if (success) {
      setTimeout(() => {
        dispatch(resetProfileFlag())
        setHasChanges(false)
      }, 3000)
    }
    if (error) {
      setTimeout(() => {
        dispatch(resetProfileFlag())
      }, 3000)
    }
  }, [dispatch, success, error])

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: userData?.firstName || "",
      lastName: userData?.lastName || "",
      phone: userData?.phone || "",
      email: userData?.email || "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .required("Please Enter Your First Name")
        .min(2, "First name must be at least 2 characters")
        .max(25, "First name must be at most 25 characters")
        .matches(/^[A-Za-z]+$/, "First name can only contain letters"),

      lastName: Yup.string()
        .required("Please Enter Your Last Name")
        .min(2, "Last name must be at least 2 characters")
        .max(25, "Last name must be at most 25 characters")
        .matches(/^[A-Za-z]+$/, "Last name can only contain letters"),

      phone: Yup.string(),

      email: Yup.string()
        .email("Invalid Email")
        .required("Please Enter Your Email"),

      newPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .max(50, "Password must be at most 50 characters"),

      confirmPassword: Yup.string().test(
        "passwords-match",
        "Passwords must match",
        function (value) {
          const { newPassword } = this.parent
          // Validate confirmPassword if either field has a value
          if (newPassword || value) {
            if (!newPassword) {
              return this.createError({
                message: "Please enter a password first",
              })
            }
            if (!value) {
              return this.createError({
                message: "Please confirm your password",
              })
            }
            if (newPassword !== value) {
              return this.createError({ message: "Passwords must match" })
            }
          }
          return true
        },
      ),
    }),
    onSubmit: async values => {
      setLoading(true)
      const { firstName, lastName, email, newPassword, confirmPassword } =
        values

      // Extra safeguard
      if (newPassword && newPassword !== confirmPassword) {
        validation.setErrors({
          confirmPassword: "Passwords do not match",
        })
        setLoading(false)
        return
      }

      const updatedData = {}
      if (firstName !== userData.firstName) updatedData.firstName = firstName
      if (lastName !== userData.lastName) updatedData.lastName = lastName
      if (email !== userData.email) updatedData.email = email
      if (newPassword) updatedData.password = newPassword

      try {
        // Upload avatar if a new file was selected
        if (avatarFile instanceof File) {
          const formData = new FormData()
          formData.append("file", avatarFile)

          const res = await axios.post(
            `${config.backendUrl}/aws/file?email=reputation-management`,
            formData,
          )
          updatedData.avatar = res.data.url // uploaded URL
        }

        // If password is being changed → require MFA
        if (newPassword) {
          setPendingProfileData(updatedData)
          setMfaModalOpen(true)
          setLoading(false)
          return
        }

        // Otherwise save directly
        dispatch(editProfile(updatedData))
      } catch (err) {
        console.error("Avatar upload failed", err)
      } finally {
        setLoading(false)
      }
    },
  })

  // Check for changes and ensure required fields are filled
  useEffect(() => {
    const { firstName, lastName, email } = validation.values

    const requiredFieldsFilled =
      firstName.trim() !== "" && lastName.trim() !== "" && email.trim() !== ""

    const changed =
      requiredFieldsFilled &&
      (validation.values.firstName !== userData?.firstName ||
        validation.values.lastName !== userData?.lastName ||
        validation.values.email !== userData?.email ||
        validation.values.newPassword !== "" ||
        validation.values.confirmPassword !== "" ||
        avatarFile !== null)

    setHasChanges(changed)
  }, [validation.values, avatarFile, userData])

  // MFA verification handler
  const handleMfaSubmit = async code => {
    setMfaError("")
    setLoading(true)
    try {
      // Call 2FA verification API using fetch
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || config.backendUrl}/auth/2fa/verify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData?.token}`,
          },
          body: JSON.stringify({ code, email: userData?.email }),
        },
      )
      const result = await response.json()
      if (response.status === 200 && result.userInfo) {
        // Proceed to save profile changes
        dispatch(editProfile(pendingProfileData))
        setMfaModalOpen(false)
        setPendingProfileData(null)
        setLoading(false)
      } else {
        setMfaError(result.message || "Invalid MFA code.")
        setLoading(false)
      }
    } catch (err) {
      setMfaError("Error verifying 2FA code.")
      setLoading(false)
    }
  }

  const handle2FaToggle = async (method, checked) => {
    if (twoFaEnabled && checked) {
      if (twoFactorMethod === "Email") {
        const response = await axios.post(`${config.backendUrl}/auth/sendMFA`, {
          userId: userData.id,
        })
      }
      // If disabling, open modal for password and key
      setDisable2faModalOpen(true)
      return
    }
    // If enabling, call the API
    const response = await axios.post(
      `${config.backendUrl}/user/2fa/setup`,
      {
        enable: true,
        method: method,
      },
      {
        headers: {
          Authorization: `Bearer ${userData.token}`,
        },
      },
    )
    if (response.status == 200) {
      setTwoFactorMethod(method)
      setTwoFaEnabled(true)

      if (method === "Authenticator") {
        setQrCodeUrl(response.data.qrCodeUrl)
        setQrModalOpen(true)
      }
    }
    console.log("He", response)
  }

  const handleDisable2faConfirm = async () => {
    setDisable2faError("")
    if (!disable2faPassword || !disable2faCode) {
      setDisable2faError("Please enter both password and authentication code.")
      return
    }

    try {
      const response = await axios.post(
        `${config.backendUrl}/user/2fa/disable`,
        {
          password: disable2faPassword,
          authCode: disable2faCode,
        },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )

      console.log("res", response)
      if (response.status === 200 && response.data.success) {
        setTwoFaEnabled(false)
        setDisable2faModalOpen(false)
        setDisable2faPassword("")
        setDisable2faCode("")
        setDisable2faError("")
      } else {
        setDisable2faError(response.data.message || "Failed to disable 2FA.")
      }
    } catch (err) {
      setDisable2faError(
        err.response.data.message || "Invalid password or authentication code.",
      )
    }
  }

  const handleAvatarChange = file => {
    if (!file) return
    const previewUrl = URL.createObjectURL(file)
    setAvatarPreview(previewUrl)
    setAvatarFile(file)
  }

  return (
    <React.Fragment>
      <SnackbarComponent err={error} success={success} />
      <MFAInput
        isOpen={mfaModalOpen}
        toggle={() => {
          setMfaModalOpen(false)
          setPendingProfileData(null)
          setMfaError("")
        }}
        onSubmit={handleMfaSubmit}
        error={mfaError}
      />
      <div className="page-content p-0">
        {/* Header Bar */}
        <div
          style={{
            backgroundColor: mode === "dark" ? "#252f40" : "#fff",
            marginTop: "0px",
            padding: "15px 20px",
            fontFamily: "Inter",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            color: mode === "dark" ? "#fff" : "#050409",
            fontWeight: "600",
            fontSize: "23px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <h1
            style={{
              fontWeight: "500",
              marginTop: "10px",
              fontSize: "20px",
            }}
          >
            Admin consoles
          </h1>
          {hasChanges && validation.isValid && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <PrimaryButton
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  backgroundColor: mode === "dark" ? "#fff" : "#fff",
                  border: "1px solid #DCDCDD",
                  color: "#000",
                  textTransform: "none",
                  width: "166px",
                  height: "46px",
                  borderRadius: "5px",
                }}
                onClick={() => {
                  validation.resetForm()
                  setAvatarFile(null)
                  setAvatarPreview(userData?.avatar || "default-avatar.png") // reset avatar
                  setHasChanges(false)
                }}
              >
                Cancel
              </PrimaryButton>
              <PrimaryButton
                style={{
                  fontSize: "14px",
                  width: "166px",
                  height: "46px",
                  fontWeight: "500",
                  backgroundColor: "#2990DF",
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: "5px",
                  padding: "0 10px !important",
                }}
                icon={<DoneOutlinedIcon style={{ fontSize: "18px" }} />}
                onClick={() => {
                  setSaveModalOpen(true)
                }}
              >
                Save Changes
              </PrimaryButton>
            </div>
          )}
        </div>
        <div
          style={{
            borderRadius: "20px",
            padding: "10px",
            backgroundColor: mode === "dark" ? "#252f40" : "#fff",
          }}
        >
          <Container
            style={{
              backgroundColor: mode === "dark" ? "#252f40" : "#fff",
            }}
            fluid
          >
            <h4
              style={{
                fontSize: "16px",
                fontWeight: "400",
                marginTop: "10px",
                color: mode === "dark" ? "#fff" : "#000",
              }}
            >
              Update Profile
            </h4>

            <Card style={{ backgroundColor: "transparent", boxShadow: "none" }}>
              <CardBody style={{ backgroundColor: "transparent !important" }}>
                {/* Avatar Section with 2FA */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    marginBottom: "40px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                    }}
                  >
                    <div style={{ position: "relative" }}>
                      <img
                        src={avatarPreview}
                        alt="Profile"
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "12px",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="avatar-upload"
                        style={{
                          color: mode === "dark" ? "#fff" : "#2990DF",
                          cursor: "pointer",
                          fontSize: "14px",
                          backgroundColor: "transparent",
                          fontWeight: "400",
                          width: "180px",
                          height: "40px",
                          marginTop: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border:
                            mode === "dark"
                              ? "2px solid #374151"
                              : "2px dashed #DCDCDD",
                          borderRadius: "5px",
                        }}
                      >
                        <ArrowUpwardOutlinedIcon
                          style={{
                            fontSize: "16px",
                            marginRight: "4px",
                            color: mode === "dark" ? "#fff" : "#2990DF",
                          }}
                        />
                        Upload Profile Picture
                      </label>

                      <Input
                        id="avatar-upload"
                        type="file"
                        name="avatar"
                        style={{ display: "none" }}
                        onChange={e => handleAvatarChange(e.target.files[0])}
                        accept="image/*"
                      />
                    </div>
                  </div>

                  {/* 2FA Toggle Section */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <Label
                        className="mb-0"
                        style={{ fontWeight: 500, fontSize: "14px" }}
                        htmlFor="2fa-switch"
                      >
                        Enable 2FA
                      </Label>
                      <Switch
                        id="2fa-switch"
                        name="2Fa"
                        checked={twoFaEnabled}
                        onClick={e =>
                          handle2FaToggle("Email", e.target.checked)
                        }
                        size="medium"
                        sx={{ transform: "scale(1)" }}
                      />
                    </div>

                    {twoFaEnabled && (
                      <ButtonGroup size="sm">
                        <Button
                          size="sm"
                          color={
                            twoFactorMethod === "Email"
                              ? "primary"
                              : "outline-primary"
                          }
                          onClick={() => {
                            if (twoFactorMethod !== "Email") {
                              handle2FaToggle("Email", false)
                            }
                          }}
                          style={{ fontSize: "12px", padding: "4px 12px" }}
                        >
                          Email
                        </Button>
                        <Button
                          size="sm"
                          color={
                            twoFactorMethod === "Authenticator"
                              ? "primary"
                              : "outline-primary"
                          }
                          onClick={() => {
                            if (twoFactorMethod !== "Authenticator") {
                              handle2FaToggle("Authenticator", false)
                            }
                          }}
                          style={{ fontSize: "12px", padding: "4px 12px" }}
                        >
                          Authenticator
                        </Button>
                      </ButtonGroup>
                    )}
                  </div>
                </div>

                {/* Form */}
                <Form
                  onSubmit={e => {
                    e.preventDefault()
                    validation.handleSubmit()
                    return false
                  }}
                >
                  {/* First & Last Name */}
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label
                          className="form-label"
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            color: mode === "dark" ? "#fff" : "#666",
                          }}
                        >
                          First Name
                        </Label>
                        <div style={{ position: "relative", height: "45px" }}>
                          <PersonOutlineOutlinedIcon
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: mode === "dark" ? "#fff" : "#666",
                              fontSize: "20px",
                            }}
                          />
                          <Input
                            name="firstName"
                            placeholder="eg.. William"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.firstName || ""}
                            invalid={
                              validation.touched.firstName &&
                              validation.errors.firstName
                                ? true
                                : false
                            }
                            style={{
                              paddingLeft: "44px",
                              height: "100%",
                              borderRadius: "8px",
                              border: "1px solid #e0e0e0",
                              color: mode === "dark" ? "#fff" : "#000",
                            }}
                          />
                        </div>
                        {validation.touched.firstName &&
                          validation.errors.firstName && (
                            <div
                              style={{
                                color: "#ef4444",
                                fontSize: "13px",
                                marginTop: "4px",
                              }}
                            >
                              {validation.errors.firstName}
                            </div>
                          )}
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="mb-3">
                        <Label
                          className="form-label"
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            color: mode === "dark" ? "#fff" : "#666",
                          }}
                        >
                          Last Name
                        </Label>
                        <div style={{ position: "relative", height: "45px" }}>
                          <PersonOutlineOutlinedIcon
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: mode === "dark" ? "#fff" : "#666",
                              fontSize: "20px",
                            }}
                          />
                          <Input
                            name="lastName"
                            placeholder="eg.. Marshal"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.lastName || ""}
                            invalid={
                              validation.touched.lastName &&
                              validation.errors.lastName
                                ? true
                                : false
                            }
                            style={{
                              paddingLeft: "44px",
                              height: "100%",
                              borderRadius: "8px",
                              border: "1px solid #e0e0e0",
                              color: mode === "dark" ? "#fff" : "#000",
                            }}
                          />
                        </div>
                        {validation.touched.lastName &&
                          validation.errors.lastName && (
                            <div
                              style={{
                                color: "#ef4444",
                                fontSize: "13px",
                                marginTop: "4px",
                              }}
                            >
                              {validation.errors.lastName}
                            </div>
                          )}
                      </div>
                    </Col>
                  </Row>

                  {/* Email */}
                  <Row>
                    <Col md={12}>
                      <div className="mb-3">
                        <Label
                          className="form-label"
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            color: mode === "dark" ? "#fff" : "#666",
                          }}
                        >
                          Email Address
                        </Label>
                        <div style={{ position: "relative", height: "45px" }}>
                          <EmailOutlinedIcon
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: mode === "dark" ? "#fff" : "#666",
                              fontSize: "20px",
                            }}
                          />
                          <Input
                            name="email"
                            placeholder="eg.. hello@imzul.com"
                            type="email"
                            disabled
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              validation.touched.email &&
                              validation.errors.email
                                ? true
                                : false
                            }
                            style={{
                              paddingLeft: "44px",
                              height: "100%",
                              borderRadius: "8px",
                              border: "1px solid #e0e0e0",
                              color: mode === "dark" ? "#fff" : "#000",
                            }}
                          />
                        </div>
                        {validation.touched.email &&
                          validation.errors.email && (
                            <div
                              style={{
                                color: "#ef4444",
                                fontSize: "13px",
                                marginTop: "4px",
                              }}
                            >
                              {validation.errors.email}
                            </div>
                          )}
                      </div>
                    </Col>
                  </Row>

                  {/* Password & Confirm Password */}
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label
                          className="form-label"
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            color: mode === "dark" ? "#fff" : "#666",
                          }}
                        >
                          Password
                        </Label>
                        <div style={{ position: "relative", height: "45px" }}>
                          <PasswordOutlinedIcon
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: mode === "dark" ? "#fff" : "#666",
                              fontSize: "20px",
                            }}
                          />
                          <Input
                            name="newPassword"
                            placeholder="eg.. ••••••••"
                            type={showPassword ? "text" : "password"}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.newPassword || ""}
                            style={{
                              paddingLeft: "44px",
                              paddingRight: "44px",
                              height: "100%",
                              borderRadius: "8px",
                              border: "1px solid #e0e0e0",
                              color: mode === "dark" ? "#fff" : "#000",
                            }}
                          />
                          <div
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: "absolute",
                              right: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              cursor: "pointer",
                              color: mode === "dark" ? "#fff" : "#666",
                            }}
                          >
                            {showPassword ? (
                              <VisibilityOffOutlinedIcon
                                style={{ fontSize: "20px" }}
                              />
                            ) : (
                              <VisibilityOutlinedIcon
                                style={{ fontSize: "20px" }}
                              />
                            )}
                          </div>
                        </div>
                        {validation.touched.newPassword &&
                          validation.errors.newPassword && (
                            <div
                              style={{
                                color: "#ef4444",
                                fontSize: "13px",
                                marginTop: "4px",
                              }}
                            >
                              {validation.errors.newPassword}
                            </div>
                          )}
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="mb-3">
                        <Label
                          className="form-label"
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            color: mode === "dark" ? "#fff" : "#666",
                          }}
                        >
                          Confirm Password
                        </Label>
                        <div style={{ position: "relative", height: "45px" }}>
                          <PasswordOutlinedIcon
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: mode === "dark" ? "#fff" : "#666",
                              fontSize: "20px",
                            }}
                          />
                          <Input
                            name="confirmPassword"
                            placeholder="eg..••••••••"
                            type={showConfirmPassword ? "text" : "password"}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.confirmPassword || ""}
                            style={{
                              paddingLeft: "44px",
                              paddingRight: "44px",
                              height: "100%",
                              borderRadius: "8px",
                              border: "1px solid #e0e0e0",
                              color: mode === "dark" ? "#fff" : "#000",
                            }}
                          />
                          <div
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            style={{
                              position: "absolute",
                              right: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              cursor: "pointer",
                              color: mode === "dark" ? "#fff" : "#666",
                            }}
                          >
                            {showConfirmPassword ? (
                              <VisibilityOffOutlinedIcon
                                style={{ fontSize: "20px" }}
                              />
                            ) : (
                              <VisibilityOutlinedIcon
                                style={{ fontSize: "20px" }}
                              />
                            )}
                          </div>
                        </div>
                        {validation.touched.confirmPassword &&
                          validation.errors.confirmPassword && (
                            <div
                              style={{
                                color: "#ef4444",
                                fontSize: "13px",
                                marginTop: "4px",
                              }}
                            >
                              {validation.errors.confirmPassword}
                            </div>
                          )}
                      </div>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Container>
        </div>
        {/* Save Modal */}
        <Modal
          isOpen={saveModalOpen}
          toggle={() => setSaveModalOpen(!saveModalOpen)}
          centered
          style={{
            maxWidth: "500px",
          }}
        >
          <div
            style={{
              backgroundColor: mode === "dark" ? "#1f2937" : "#fff",
              borderRadius: "12px",
              padding: "0",
            }}
          >
            <ModalHeader
              toggle={() => setSaveModalOpen(false)}
              style={{
                backgroundColor: mode === "dark" ? "#1f2937" : "#fff",
                color: mode === "dark" ? "#fff" : "#000",
                fontWeight: "600",
                fontSize: "18px",
                borderBottom:
                  mode === "dark" ? "1px solid #374151" : "1px solid #e5e7eb",
                paddingBottom: "16px",
              }}
            >
              Confirmation
            </ModalHeader>
            <ModalBody
              style={{
                backgroundColor: mode === "dark" ? "#1f2937" : "#fff",
                textAlign: "center",
                padding: "30px 40px",
              }}
            >
              <div
                style={{
                  width: "120px",
                  height: "80px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px",
                }}
              >
                <CheckCircleOutlineOutlinedIcon
                  style={{
                    fontSize: "80px",
                    color: mode === "dark" ? "#fff" : "#2990DF",
                  }}
                />
              </div>
              <h5
                style={{
                  color: mode === "dark" ? "#fff" : "#000",
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "12px",
                }}
              >
                Are you sure you want to update your account?
              </h5>
              <p
                style={{
                  color: mode === "dark" ? "#9ca3af" : "#666",
                  fontSize: "12px",
                  marginBottom: "0",
                }}
              >
                Your account will be updated, and you can downgrade at any time.
              </p>
            </ModalBody>
            <ModalFooter
              style={{
                backgroundColor: mode === "dark" ? "#1f2937" : "#fff",
                paddingVertical: "10px",
                paddingHorizontal: "40px",
                justifyContent: "center",
                gap: "12px",
                borderTop:
                  mode === "dark" ? "1px solid #374151" : "1px solid #e5e7eb",
              }}
            >
              <PrimaryButton
                onClick={() => setSaveModalOpen(false)}
                style={{
                  backgroundColor: "white",
                  border:
                    mode === "dark" ? "1px solid #374151" : "1px solid #e0e0e0",
                  color: mode === "dark" ? "#000" : "#000",
                  fontSize: "14px",
                  fontWeight: "500",
                  width: "166px",
                  height: "46px",
                  minWidth: "120px",
                  borderRadius: "5px",
                }}
              >
                Cancel
              </PrimaryButton>
              <PrimaryButton
                onClick={() => {
                  validation.handleSubmit()
                  setSaveModalOpen(false)
                }}
                style={{
                  backgroundColor: "#2990DF",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: "500",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "166px",
                  height: "46px",
                  minWidth: "120px",
                  borderRadius: "5px",
                  textTransform: "none",
                  padding: "0 10px !important",
                }}
              >
                Save Changes
                <DoneOutlinedIcon style={{ fontSize: "18px" }} />
              </PrimaryButton>
            </ModalFooter>
          </div>
        </Modal>

        {/* 2FA Modals */}
        <Modal
          isOpen={disable2faModalOpen}
          toggle={() => setDisable2faModalOpen(!disable2faModalOpen)}
        >
          <ModalHeader
            toggle={() => setDisable2faModalOpen(!disable2faModalOpen)}
          >
            Disable 2FA Confirmation
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label>Password</Label>
              <Input
                type="password"
                value={disable2faPassword}
                onChange={e => setDisable2faPassword(e.target.value)}
                placeholder="Enter your password"
              />
            </div>
            <div className="mb-3">
              <Label>Authentication Code</Label>
              <Input
                type="text"
                value={disable2faCode}
                onChange={e => setDisable2faCode(e.target.value)}
                placeholder="Enter your 2FA code"
              />
            </div>
            {disable2faError && <Alert color="danger">{disable2faError}</Alert>}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setDisable2faModalOpen(false)}
            >
              Cancel
            </Button>
            <Button color="danger" onClick={handleDisable2faConfirm}>
              Disable 2FA
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={qrModalOpen} toggle={() => setQrModalOpen(!qrModalOpen)}>
          <ModalHeader toggle={() => setQrModalOpen(!qrModalOpen)}>
            2FA QR Code
          </ModalHeader>
          <ModalBody className="text-center">
            {qrCodeUrl ? (
              <img
                src={qrCodeUrl}
                alt="2FA QR Code"
                style={{ maxWidth: "100%", marginBottom: 16 }}
              />
            ) : (
              <p>No QR code available. Please enable 2FA to generate one.</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setQrModalOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  )
}

export default withRouter(UserProfile)
